import { defineStore } from 'pinia'
import { useFsfStore } from '@/components/fsf/stores/fsf.store'
import type {
  IInboundFlight,
  IOutboundFlight,
  IOverallAPIResponse,
  TCheapestPrice,
  IOutboundFlightOnewayQueryParams,
  IOutboundFlightReturnQueryParams
} from '@/components/fsf/types'
import { OUTBOUND, RETURN } from '@/utils/constants/fsf'

export const useFlightStore = defineStore('FsfFlights', () => {
  const fsfStore = useFsfStore()

  const outboundFlights = ref<IOutboundFlight[]>()
  const inboundFlights = ref<IInboundFlight[]>()
  const isFlightDataPending = ref(false)
  const cheapestOutboundPrice = ref<TCheapestPrice>(null)
  const cheapestInboundPrice = ref<TCheapestPrice>(null)
  const cheapestOutboundPricesByMonth = ref<Record<string, TCheapestPrice>>()
  const cheapestInboundPricesByMonth = ref<Record<string, TCheapestPrice>>()
  const monthPricing = ref<Pick<IOverallAPIResponse, 'outbound' | 'inbound'>>({ outbound: [], inbound: [] })
  const isMonthPricingPending = ref(false)
  const hasFetchedFlights = ref(false)

  const clearMonthPricingData = () => {
    monthPricing.value.inbound = []
    monthPricing.value.outbound = []
    cheapestOutboundPrice.value = null
    cheapestInboundPrice.value = null
    cheapestOutboundPricesByMonth.value = undefined
    cheapestInboundPricesByMonth.value = undefined
  }

  const getOverallFlightPricingPerMonth = async () => {
    clearMonthPricingData()

    try {
      isMonthPricingPending.value = true

      const response = await $fetch('/api/fsf/overall', {
        params: {
          flightMode: fsfStore.currentFlightMode,
          origin: fsfStore.origin,
          destin: fsfStore.destin
        },
        credentials: 'omit'
      })

      if (response.success && 'data' in response && response.data) {
        monthPricing.value.inbound = response.data.inbound
        monthPricing.value.outbound = response.data.outbound
        cheapestOutboundPrice.value = response.data.cheapestOutboundPrice
        cheapestInboundPrice.value = response.data.cheapestInboundPrice
      }

      isMonthPricingPending.value = false
    } catch (e) {
      isMonthPricingPending.value = false
      console.error(e)
    }
  }

  const getOutboundFlights = async () => {
    outboundFlights.value = []
    try {
      if (!fsfStore.origin || !fsfStore.destin) {
        return
      }

      isFlightDataPending.value = true

      await getOverallFlightPricingPerMonth()

      type Params = IOutboundFlightOnewayQueryParams | IOutboundFlightReturnQueryParams
      let params: Params = {
        flightMode: fsfStore.currentFlightMode,
        origin: fsfStore.origin,
        destin: fsfStore.destin
      }

      if (fsfStore.currentFlightMode === RETURN) {
        const { startDate, endDate } = fsfStore.currentDateRange

        params = {
          ...params,
          startDate,
          endDate
        }
      }

      const response = await $fetch('/api/fsf/outbound', { params, credentials: 'omit' })

      if (response.success && 'data' in response && response.data) {
        outboundFlights.value = response.data

        cheapestOutboundPricesByMonth.value = getCheapestInboundPrice(response.data)

        if (fsfStore.hideFsfPrices) {
          outboundFlights.value = outboundFlights.value?.map((flight) => ({
            ...flight,
            price: null
          }))
        }
      }

      isFlightDataPending.value = false
    } catch (e) {
      console.error('Error fetching outbound flights:', e)
      isFlightDataPending.value = false
      return e
    }
  }

  const getCheapestPriceByMonth = <T extends { date: string }>(
    flights: T[],
    getPrice: (flight: T) => TCheapestPrice
  ) => {
    const cheapest: Record<string, TCheapestPrice> = {}

    for (const flight of flights) {
      const month = flight.date.slice(0, 7)
      const price = getPrice(flight)
      const current = cheapest[month]

      if (current === undefined || (price !== null && (current === null || price < current))) {
        cheapest[month] = price
      }
    }

    return cheapest
  }

  const getCheapestInboundPrice = <T extends { date: string; price: TCheapestPrice }>(flights: T[]) => {
    return getCheapestPriceByMonth(flights, (flight) => flight.price)
  }

  const getInboundFlights = async (date: string) => {
    inboundFlights.value = []

    try {
      isFlightDataPending.value = true
      const response = await $fetch('/api/fsf/inbound', {
        params: {
          startDate: date,
          origin: fsfStore.origin,
          destin: fsfStore.destin
        },
        credentials: 'omit'
      })

      if (response.success && 'data' in response && response.data) {
        inboundFlights.value = response.data.flights

        cheapestOutboundPrice.value = response.data.cheapestOutboundPrice
        cheapestInboundPrice.value = response.data.cheapestInboundPrice

        cheapestInboundPricesByMonth.value = getCheapestInboundPrice(response.data.flights)

        if (fsfStore.hideFsfPrices) {
          inboundFlights.value = inboundFlights.value?.map((flight) => ({
            ...flight,
            price: null,
            outboundPrice: null
          }))

          cheapestOutboundPrice.value = null
          cheapestInboundPrice.value = null
        }
      }

      isFlightDataPending.value = false
    } catch (e) {
      console.error('Error fetching inbound flights:', e)
      isFlightDataPending.value = false
      return e
    }
  }

  const currentCheapestPrice = computed(() =>
    fsfStore.isOutboundFlightType ? cheapestOutboundPrice.value : cheapestInboundPrice.value
  )

  const selectedOutboundFlight = computed(() => {
    const flight = outboundFlights.value?.find((flight) => flight.date === fsfStore.selectedStartDate)

    if (selectedInboundFlight.value && flight?.price !== selectedInboundFlight.value?.outboundPrice) {
      return {
        ...flight,
        updatedPrice: selectedInboundFlight.value?.outboundPrice
      }
    }

    return flight
  })

  const selectedInboundFlight = computed(() =>
    inboundFlights.value?.find((flight) => flight.date === fsfStore.selectedEndDate)
  )

  const removePrices = () => {
    inboundFlights.value = inboundFlights.value?.map((flight) => ({
      ...flight,
      price: null,
      outboundPrice: null
    }))

    outboundFlights.value = outboundFlights.value?.map((flight) => ({
      ...flight,
      price: null
    }))

    cheapestOutboundPrice.value = null
    cheapestInboundPrice.value = null
  }

  watch(
    () => [fsfStore.currentFlightMode, fsfStore.origin, fsfStore.destin],
    () => {
      fsfStore.currentFlightType = OUTBOUND
      getOutboundFlights()
    },
    { immediate: true }
  )

  watch(
    () => hasFetchedFlights.value,
    (hasFetchedFlights) => {
      hasFetchedFlights && fsfStore.hideFsfPrices && removePrices()
    },
    { immediate: true }
  )

  return {
    outboundFlights,
    getOutboundFlights,
    inboundFlights,
    getInboundFlights,
    isFlightDataPending,
    cheapestOutboundPricesByMonth,
    cheapestInboundPricesByMonth,
    currentCheapestPrice,
    monthPricing,
    isMonthPricingPending,
    selectedOutboundFlight,
    selectedInboundFlight,
    clearMonthPricingData,
    hasFetchedFlights
  }
})
